"use client";

import { BadgeCheck, Headphones, IdCard } from "@/components/icons";
import {
  BadgesSection,
  ProductAnalyticsSection,
  ProductSessionBuilderSection,
  TrustedBySection,
} from "@/components/sections";
import AtAGlance from "@/components/sections/products/shared/at-a-glance";
import ProductHeroRibbon from "@/components/sections/products/shared/hero-ribbon";
import FaqSection from "@/components/sections/products/shared/faq";
import ProxyComparisonDe from "@/components/sections/de/shared/proxy-comparison";
import CompareAlternativesDe from "@/components/sections/de/shared/compare-alternatives";
import ReviewsDe from "@/components/sections/de/shared/reviews";
import PricingCtaDe from "@/components/sections/de/shared/pricing-cta";
import { LanguageSwitchLink } from "@/components/ui";
import Link from "next/link";
import { ROUTES, STATS } from "@/lib/constants";
import { STATS_IPS_DE } from "@/components/sections/de/shared/stats-de";
import ResidentialHowItWorksDe from "./parts/how-it-works";
import ResidentialSessionModesDe from "./parts/session-modes";
import ResidentialPricingFeaturesDe from "./parts/pricing-features";
import ResidentialProductHighlightsDe from "./parts/product-highlights";
import ResidentialFitCheckDe from "./parts/fit-check";
import ResidentialWhoItsForDe from "./parts/who-its-for";
import ResidentialSetupGuideDe from "./parts/setup-guide";
import ResidentialForDevelopersDe from "./parts/for-developers";
import ResidentialUseCasesDe from "./parts/use-cases";
import RelatedGuidesDe from "./parts/related-guides";
import { RESIDENTIAL_PRODUCT_FAQS_DE } from "./parts/faq-data";

/* Exact structural clone of /proxy-server/residential-proxies, translated
   section by section — same order, same CTAs. Two deliberate exceptions,
   left in English: ProductAnalyticsSection and ProductSessionBuilderSection
   are interactive mock-ups of the (English-only) real dashboard UI;
   translating the demo would misrepresent what the actual product looks
   like, same principle as not claiming German-language support. */

const heroBenefits = [
  { label: "KYC erforderlich", icon: IdCard },
  { label: "30 Tage Geld-zurück-Garantie", icon: BadgeCheck },
  { label: "Premium-Support", icon: Headphones },
];

export default function ResidentialProxiesDePage() {
  return (
    <main id="main-content" tabIndex={-1} className="relative">
      <LanguageSwitchLink
        href={ROUTES.productResidentialProxies}
        hrefLang="en"
        label="English version"
      />

      <ProductHeroRibbon
        headingId="residential-product-hero-heading"
        headingLine1="Residential Proxies"
        headingLine2={`in ${STATS.countries} Ländern`}
        description={`Zugriff auf ${STATS_IPS_DE} ethisch bezogene Residential-IPs in ${STATS.countries} Ländern. Targeting nach Land, Stadt oder ISP, mit rotierenden oder sticky Sessions, ab $25/Monat.`}
        benefits={heroBenefits}
        ctas={[
          { label: "Pläne ansehen", href: ROUTES.pricingResidentialProxies },
          { label: "Setup-Anleitung lesen", href: "#residential-setup-guide-heading" },
        ]}
      />

      <AtAGlance
        headingId="residential-at-a-glance-heading"
        heading="Proxidize Residential Proxies auf einen Blick"
        intro={
          <>
            {STATS_IPS_DE} ethisch bezogene Residential-IPs mit rotierenden und sticky Sessions.
            Alle{" "}
            <Link
              href={ROUTES.pricingResidentialProxies}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              Preise und Pläne für Residential Proxies
            </Link>{" "}
            ansehen, einschließlich Mindestkauf, Mengenstaffeln, Übertrag und
            Rückerstattungsbedingungen.
          </>
        }
        rows={[
          { label: "Startpreis", value: "$1/GB, Datenvolumen wird übertragen" },
          { label: "Mindestkauf", value: "$25/Monat für 25 GB" },
          { label: "Netzwerk", value: `${STATS_IPS_DE} Residential-IPs` },
          { label: "Abdeckung", value: `${STATS.countries} Länder` },
          { label: "Targeting", value: "Land, Stadt, ISP" },
          { label: "Sessions", value: "Rotierend oder sticky" },
          { label: "Protokolle", value: "HTTP, HTTPS, SOCKS5" },
          { label: "Authentifizierung", value: "Benutzername/Passwort" },
          { label: "Verlängerung", value: "Monatlich, bis gekündigt wird" },
          { label: "KYC", value: "Vor Proxy-Zugriff erforderlich" },
          { label: "Kostenloser Test", value: "Keiner — Kontoerstellung ist kostenlos" },
          { label: "Rückerstattung", value: "30 Tage, bedingt, ≤10 GB verbraucht" },
        ]}
      />

      <ResidentialHowItWorksDe />
      <ResidentialSessionModesDe />

      <div className="sm:hidden">
        <TrustedBySection />
      </div>

      <ResidentialPricingFeaturesDe />
      <ResidentialProductHighlightsDe />
      <ResidentialFitCheckDe />
      <ResidentialWhoItsForDe />

      <ProductAnalyticsSection variant="residential" />
      <ProductSessionBuilderSection variant="residential" />

      <ResidentialSetupGuideDe />
      <ResidentialForDevelopersDe />
      <ResidentialUseCasesDe />
      <RelatedGuidesDe />
      <ProxyComparisonDe variant="residential" />

      <ReviewsDe excludeProduct="mobile" />
      <BadgesSection />

      <FaqSection
        faqs={RESIDENTIAL_PRODUCT_FAQS_DE}
        heading="Häufig gestellte Fragen"
        description="Antworten zu Preisen, Sourcing, Targeting und Verifizierung für Residential Proxies."
        headingId="de-residential-faq"
        contactLabel="Unser Team kontaktieren"
      />

      <CompareAlternativesDe />
      <PricingCtaDe
        id="residential-cta-heading"
        heading={`${STATS_IPS_DE} IPs. ${STATS.countries} Länder. Ein Klick zum Start.`}
      />
    </main>
  );
}
